import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  Search,
  Package,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Tag,
  Layers,
  AlertCircle
} from 'lucide-react';
import { useAlerts } from '../../hooks/useAlerts';
import { Alert, ALERT_TYPE_INFO, AlertType, AlertProductDetail } from '../../types/alerts';
import { AlertConfigModal } from './AlertConfigModal';
import api from '../../utils/api';

interface ProductsWithAlertsListProps {
  currentTableName?: string;
  onProductClick?: (productId: string) => void;
}

export const ProductsWithAlertsList: React.FC<ProductsWithAlertsListProps> = ({
  currentTableName,
  onProductClick
}) => {
  const { t } = useTranslation();
  const { alerts, loading, error, deleteAlert, toggleAlertActive } = useAlerts();

  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [loadedProducts, setLoadedProducts] = useState<Record<string, AlertProductDetail>>({});
  const loadingProductsRef = useRef<Set<string>>(new Set());
  const [editingAlert, setEditingAlert] = useState<Alert | undefined>(undefined);
  const [creatingForProduct, setCreatingForProduct] = useState<{ productId: string; productDetail?: AlertProductDetail } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Agrupar alertas por producto
  const productAlerts = useMemo(() => {
    const grouped: Record<string, Alert[]> = {};

    alerts.forEach(alert => {
      if (alert.assignmentType === 'individual' && alert.productIds) {
        alert.productIds.forEach(productId => {
          if (!grouped[productId]) {
            grouped[productId] = [];
          }
          grouped[productId].push(alert);
        });
      }
    });

    return Object.entries(grouped)
      .map(([productId, productAlerts]) => {
        // Buscar productDetail en las alertas o en loadedProducts
        let productDetail: AlertProductDetail | undefined;
        for (const alert of productAlerts) {
          const detail = alert.productDetails?.find(p => p.id_catalogo === productId);
          if (detail) {
            productDetail = detail;
            break;
          }
        }
        if (!productDetail && loadedProducts[productId]) {
          productDetail = loadedProducts[productId];
        }

        return {
          productId,
          productDetail,
          alerts: productAlerts
        };
      })
      .sort((a, b) => b.alerts.length - a.alerts.length);
  }, [alerts, loadedProducts]);

  // Cargar datos de productos que no tienen productDetails
  useEffect(() => {
    if (!currentTableName) return;

    const productIdsToLoad: string[] = [];
    productAlerts.forEach(({ productId, productDetail }) => {
      if (!productDetail && !loadedProducts[productId] && !loadingProductsRef.current.has(productId)) {
        productIdsToLoad.push(productId);
        loadingProductsRef.current.add(productId);
      }
    });

    if (productIdsToLoad.length === 0) return;

    const loadProducts = async () => {
      // Cargar cada producto individualmente usando búsqueda exacta por ID
      const newLoadedProducts: Record<string, AlertProductDetail> = {};

      await Promise.all(productIdsToLoad.map(async (productId) => {
        try {
          // Buscar por id_catalogo exacto usando global_search
          const response = await api.get(`/tables/${currentTableName}/data`, {
            params: {
              global_search: productId,
              limit: 50
            }
          });

          const products = response.data.data || [];
          // Buscar coincidencia exacta del id_catalogo
          const exactMatch = products.find((p: any) => {
            const id = String(p.id_catalogo || p.ID_CATALOGO || '');
            return id === productId;
          });

          if (exactMatch) {
            newLoadedProducts[productId] = {
              id_catalogo: productId,
              title: exactMatch.title || exactMatch.TITLE || exactMatch.titulo || productId,
              sitio: exactMatch.sitio || exactMatch.SITIO,
              imagen: exactMatch.imagen || exactMatch.IMAGEN
            };
          }
        } catch (err) {
          console.error(`Error loading product ${productId}:`, err);
        }
      }));

      if (Object.keys(newLoadedProducts).length > 0) {
        setLoadedProducts(prev => ({ ...prev, ...newLoadedProducts }));
      }
    };

    loadProducts();
  }, [productAlerts, currentTableName]);

  // Alertas por SKU
  const skuAlerts = useMemo(() => {
    return alerts.filter(alert => alert.assignmentType === 'sku');
  }, [alerts]);

  // Alertas por grupo
  const groupAlerts = useMemo(() => {
    return alerts.filter(alert => alert.assignmentType === 'comparison_group');
  }, [alerts]);

  // Filtrar productos
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return productAlerts;
    const term = searchTerm.toLowerCase();
    return productAlerts.filter(({ productId, productDetail }) =>
      productId.toLowerCase().includes(term) ||
      productDetail?.title?.toLowerCase().includes(term) ||
      productDetail?.sitio?.toLowerCase().includes(term)
    );
  }, [productAlerts, searchTerm]);

  // Toggle expansión
  const toggleProduct = (productId: string) => {
    setExpandedProducts(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  // Obtener label del tipo de alerta
  const getAlertTypeLabel = (type: AlertType): string => {
    const info = ALERT_TYPE_INFO.find(t => t.type === type);
    return info ? t(info.labelKey) : type;
  };

  // Manejar toggle activo
  const handleToggleActive = async (alertId: string) => {
    setActionLoading(alertId);
    try {
      await toggleAlertActive(alertId);
    } catch (err) {
      console.error('Error toggling alert:', err);
    } finally {
      setActionLoading(null);
    }
  };

  // Manejar eliminación
  const handleDelete = async (alertId: string) => {
    setActionLoading(alertId);
    try {
      await deleteAlert(alertId);
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting alert:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12 text-red-500">
        <AlertCircle className="h-5 w-5 mr-2" />
        {error}
      </div>
    );
  }

  const totalProductsWithAlerts = productAlerts.length;

  return (
    <div className="space-y-6">
      {/* Resumen */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-blue-600">{totalProductsWithAlerts}</p>
          <p className="text-sm text-gray-500">{t('alerts.products_with_alerts')}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-purple-600">{skuAlerts.length}</p>
          <p className="text-sm text-gray-500">{t('alerts.sku_alerts')}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-green-600">{groupAlerts.length}</p>
          <p className="text-sm text-gray-500">{t('alerts.group_alerts')}</p>
        </div>
      </div>

      {/* Búsqueda */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          placeholder={t('alerts.search_product')}
        />
      </div>

      {/* Alertas por SKU */}
      {skuAlerts.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h3 className="font-medium text-purple-900 mb-3 flex items-center">
            <Tag className="h-5 w-5 mr-2" />
            {t('alerts.sku_based_alerts')}
          </h3>
          <div className="space-y-2">
            {skuAlerts.map(alert => (
              <div
                key={alert.id}
                className="flex items-center justify-between p-3 bg-white rounded border border-purple-100"
              >
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{alert.name}</p>
                  <p className="text-xs text-gray-500">
                    SKU: <span className="font-mono">{alert.skuCode}</span>
                    {' • '}
                    {getAlertTypeLabel(alert.alertType)}
                    {alert.triggerConfig.targetValue !== undefined && (
                      <span className="ml-2 text-blue-600">
                        ${alert.triggerConfig.targetValue.toLocaleString()}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleToggleActive(alert.id!)}
                    disabled={actionLoading === alert.id}
                    className={`p-1.5 rounded transition-colors ${
                      alert.isActive ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'
                    }`}
                  >
                    {actionLoading === alert.id ? (
                      <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                    ) : alert.isActive ? (
                      <ToggleRight className="h-4 w-4" />
                    ) : (
                      <ToggleLeft className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => setEditingAlert(alert)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  {deleteConfirm === alert.id ? (
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleDelete(alert.id!)}
                        disabled={actionLoading === alert.id}
                        className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        {actionLoading === alert.id ? '...' : 'Sí'}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(alert.id!)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alertas por grupo */}
      {groupAlerts.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="font-medium text-green-900 mb-3 flex items-center">
            <Layers className="h-5 w-5 mr-2" />
            {t('alerts.group_based_alerts')}
          </h3>
          <div className="space-y-2">
            {groupAlerts.map(alert => (
              <div
                key={alert.id}
                className="flex items-center justify-between p-3 bg-white rounded border border-green-100"
              >
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{alert.name}</p>
                  <p className="text-xs text-gray-500">
                    {t('alerts.comparison_group')}
                    {' • '}
                    {getAlertTypeLabel(alert.alertType)}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleToggleActive(alert.id!)}
                    disabled={actionLoading === alert.id}
                    className={`p-1.5 rounded transition-colors ${
                      alert.isActive ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'
                    }`}
                  >
                    {actionLoading === alert.id ? (
                      <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                    ) : alert.isActive ? (
                      <ToggleRight className="h-4 w-4" />
                    ) : (
                      <ToggleLeft className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => setEditingAlert(alert)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  {deleteConfirm === alert.id ? (
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleDelete(alert.id!)}
                        disabled={actionLoading === alert.id}
                        className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        {actionLoading === alert.id ? '...' : 'Sí'}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(alert.id!)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista de productos individuales */}
      <div>
        <h3 className="font-medium text-gray-900 mb-3 flex items-center">
          <Package className="h-5 w-5 mr-2 text-blue-500" />
          {t('alerts.individual_products')} ({filteredProducts.length})
        </h3>

        {filteredProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400 bg-gray-50 rounded-lg">
            <Bell className="h-10 w-10 mb-2" />
            <p>{t('alerts.no_products_with_alerts')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredProducts.map(({ productId, productDetail, alerts: productAlerts }) => (
              <div
                key={productId}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleProduct(productId)}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {expandedProducts.has(productId) ? (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                    {productDetail?.imagen ? (
                      <img
                        src={productDetail.imagen}
                        alt=""
                        className="h-10 w-10 object-cover rounded"
                      />
                    ) : (
                      <div className="h-10 w-10 bg-gray-100 rounded flex items-center justify-center">
                        <Package className="h-5 w-5 text-gray-400" />
                      </div>
                    )}
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900 truncate max-w-[300px]">
                        {productDetail?.title || productId}
                      </p>
                      {productDetail?.sitio && (
                        <p className="text-xs text-gray-500">{productDetail.sitio}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                      {productAlerts.length} {productAlerts.length === 1 ? 'alerta' : 'alertas'}
                    </span>
                    {onProductClick && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onProductClick(productId);
                        }}
                        className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                        title={t('alerts.view_product')}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </button>

                {expandedProducts.has(productId) && (
                  <div className="border-t border-gray-100 p-3 bg-gray-50">
                    <div className="space-y-2">
                      {productAlerts.map(alert => (
                        <div
                          key={alert.id}
                          className="flex items-center justify-between p-2 bg-white rounded border border-gray-200"
                        >
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 text-sm">{alert.name}</p>
                            <p className="text-xs text-gray-500">
                              {getAlertTypeLabel(alert.alertType)}
                              {alert.triggerConfig.targetValue !== undefined && (
                                <span className="ml-2 text-blue-600">
                                  ${alert.triggerConfig.targetValue.toLocaleString()}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleToggleActive(alert.id!)}
                              disabled={actionLoading === alert.id}
                              className={`p-1.5 rounded transition-colors ${
                                alert.isActive ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'
                              }`}
                            >
                              {actionLoading === alert.id ? (
                                <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                              ) : alert.isActive ? (
                                <ToggleRight className="h-4 w-4" />
                              ) : (
                                <ToggleLeft className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => setEditingAlert(alert)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            {deleteConfirm === alert.id ? (
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => handleDelete(alert.id!)}
                                  disabled={actionLoading === alert.id}
                                  className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                                >
                                  {actionLoading === alert.id ? '...' : 'Sí'}
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(null)}
                                  className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeleteConfirm(alert.id!)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Botón para agregar nueva alerta al producto */}
                      <button
                        onClick={() => setCreatingForProduct({ productId, productDetail })}
                        className="w-full flex items-center justify-center space-x-2 p-2 border border-dashed border-gray-300 rounded text-gray-500 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                      >
                        <Plus className="h-4 w-4" />
                        <span className="text-sm">Agregar alerta</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de edición */}
      {editingAlert && (
        <AlertConfigModal
          isOpen={!!editingAlert}
          onClose={() => setEditingAlert(undefined)}
          editingAlert={editingAlert}
          currentTableName={currentTableName}
        />
      )}

      {/* Modal de creación para producto específico */}
      {creatingForProduct && (
        <AlertConfigModal
          isOpen={!!creatingForProduct}
          onClose={() => setCreatingForProduct(null)}
          currentTableName={currentTableName}
          preselectedProducts={[creatingForProduct.productDetail ? {
            id_catalogo: creatingForProduct.productId,
            title: creatingForProduct.productDetail.title,
            sitio: creatingForProduct.productDetail.sitio || '',
            imagen: creatingForProduct.productDetail.imagen
          } : {
            id_catalogo: creatingForProduct.productId,
            title: creatingForProduct.productId,
            sitio: ''
          }]}
        />
      )}
    </div>
  );
};
